import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import FormContainer from './FormContainer';
import { careersFormSchema, CareersFormData } from '@/lib/formSchemas';
import { careerRoles } from '@/lib/formConfig';

/**
 * CareersForm - Job application form
 * 
 * Used on: Careers page only
 */

interface CareersFormProps {
  title?: string;
  description?: string;
  className?: string;
}

const CareersForm = ({
  title = "Apply to join us",
  description = "We are always looking for talented people who think in systems.",
  className = '',
}: CareersFormProps) => {
  const { toast } = useToast();

  const form = useForm<CareersFormData>({
    resolver: zodResolver(careersFormSchema),
    defaultValues: {
      fullName: '',
      email: '',
      roleApplying: '',
      linkedinUrl: '',
      coverLetter: '',
    },
  });

  const onSubmit = async (data: CareersFormData) => {
    console.log('Careers form submitted:', { ...data, email: '[REDACTED]' });
    
    toast({
      title: "Application received",
      description: "Thank you for your interest. We will review your application and be in touch.",
    });
    
    form.reset();
  };

  return (
    <FormContainer title={title} description={description} className={className}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="roleApplying"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role Applying For *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-background border border-border z-50">
                    {careerRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="linkedinUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>LinkedIn or Portfolio URL</FormLabel>
                <FormControl>
                  <Input type="url" placeholder="https://linkedin.com/in/yourprofile" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coverLetter"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Why XperfoMax?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Tell us why you want to join and what makes you a great fit..."
                    className="min-h-[150px] resize-none"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" size="lg" className="group">
            Apply now
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </form>
      </Form>
    </FormContainer>
  );
};

export default CareersForm;
